import { useEffect, useState } from 'react';
import axios from 'axios';
import { useParams, Link } from 'react-router-dom';
import {
  ArrowLeft, Star, Wifi, WifiOff, Tag, Server, Radio, History, FolderOpen, Gauge, UserCircle2,
  ShieldCheck, ShieldAlert, Package, Layers, ShieldOff, ScreenShare,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';

// Policy tool di accesso remoto dell'asset (2026-09-24, vedi ingestion/remoteAccess.ts):
// eredita dal tenant / negato / consentito con lista di tool. Mostra anche l'ultimo tool visto
// e da quale segnale, così chi imposta la policy sa cosa è stato osservato davvero.
interface RemoteTool { key: string; name: string; enabled: boolean }
function RemoteAccessCard({ asset, onSaved }: { asset: AssetDetail; onSaved: (a: Partial<AssetDetail>) => void }) {
  const { isAdmin } = useAuth();
  const [tools, setTools] = useState<RemoteTool[]>([]);
  const [tenant, setTenant] = useState<{ policy: 'info' | 'deny' | 'allow'; tools: string[] } | null>(null);
  const [policy, setPolicy] = useState<'inherit' | 'deny' | 'allow'>(asset.remote_access_policy || 'inherit');
  const [selected, setSelected] = useState<string[]>(asset.remote_access_tools || []);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  useEffect(() => {
    axios.get('/api/remote-access/tools').then(r => setTools(r.data.data.filter((t: RemoteTool) => t.enabled)));
    axios.get(`/api/remote-access/tenant/${asset.tenant_id}`).then(r => setTenant(r.data.data)).catch(() => setTenant(null));
  }, [asset.id, asset.tenant_id]);
  async function save() {
    setSaving(true);
    try {
      const r = await axios.put(`/api/assets/${asset.id}`, { remoteAccessPolicy: policy, remoteAccessTools: policy === 'allow' ? selected : [] });
      onSaved({ remote_access_policy: r.data.data.remote_access_policy, remote_access_tools: r.data.data.remote_access_tools });
      setSaved(true); setTimeout(() => setSaved(false), 2000);
    } finally { setSaving(false); }
  }
  const toolName = (key: string | null) => tools.find(t => t.key === key)?.name || key || '—';
  const inherited = tenant
    ? tenant.policy === 'info' ? 'tenant default: informational only (no enforcement until a policy is set)'
      : tenant.policy === 'deny' ? 'tenant default: remote access denied'
        : `tenant default: allowed — ${tenant.tools.map(toolName).join(', ') || 'no tool'}`
    : 'tenant default';
  return (
    <div className="card p-4">
      <h2 className="font-medium text-sm text-gray-900 mb-2 inline-flex items-center gap-2"><ScreenShare size={15} /> Remote access policy</h2>
      <p className="text-xs text-gray-500 mb-3">
        Last seen: {asset.remote_access_last_tool
          ? <><span className="font-medium text-gray-800">{toolName(asset.remote_access_last_tool)}</span> via {asset.remote_access_last_signal} on {asset.remote_access_last_seen_at ? new Date(asset.remote_access_last_seen_at).toLocaleString('en-GB') : '—'}</>
          : 'no remote access tool observed on this asset yet'}.
      </p>
      <div className="flex flex-wrap items-start gap-4 text-sm">
        <label className="block">
          <span className="text-xs text-gray-500">Policy</span>
          <select disabled={!isAdmin} className="input-field mt-1 w-64" value={policy} onChange={e => setPolicy(e.target.value as typeof policy)}>
            <option value="inherit">Inherit ({inherited})</option>
            <option value="deny">Deny — any remote access tool is a critical incident</option>
            <option value="allow">Allow only the selected tools</option>
          </select>
        </label>
        {policy === 'allow' && (
          <div>
            <span className="text-xs text-gray-500">Allowed tools</span>
            <div className="mt-1 grid grid-cols-2 gap-x-4 gap-y-1">
              {tools.map(t => (
                <label key={t.key} className="inline-flex items-center gap-1.5 text-xs">
                  <input type="checkbox" disabled={!isAdmin} checked={selected.includes(t.key)}
                    onChange={e => setSelected(e.target.checked ? [...selected, t.key] : selected.filter(k => k !== t.key))} />
                  {t.name}
                </label>
              ))}
            </div>
          </div>
        )}
        {isAdmin
          ? <button onClick={save} disabled={saving} className="btn-primary text-xs self-end disabled:opacity-50">{saving ? 'Saving…' : saved ? 'Saved' : 'Save policy'}</button>
          : <p className="text-xs text-gray-500 self-end">Only an admin can change this policy.</p>}
      </div>
    </div>
  );
}

interface VmwareDetail {
  integration_id: number;
  integration_label: string | null;
  integration_url: string;
  kind: 'host' | 'vm' | 'vcenter';
  moref: string;
  name: string;
  product_name: string | null;
  product_version: string | null;
  product_build: string | null;
  connection_state: string | null;
  maintenance_mode: boolean | null;
  boot_time: string | null;
  hardware_vendor: string | null;
  hardware_model: string | null;
  hardware_uuid: string | null;
  cpu_model: string | null;
  cpu_count: number | null;
  memory_mb: number | null;
  lockdown_mode: string | null;
  ssh_running: boolean | null;
  ssh_policy: string | null;
  shell_running: boolean | null;
  shell_policy: string | null;
  ntp_servers: string[] | null;
  syslog_host: string | null;
  exec_installed_only: boolean | null;
  mob_enabled: boolean | null;
  account_lock_failures: number | null;
  management_ip: string | null;
  power_state: string | null;
  guest_full_name: string | null;
  guest_hostname: string | null;
  guest_ip: string | null;
  tools_status: string | null;
  tools_version: string | null;
  tools_version_status: string | null;
  tools_running: boolean | null;
  hardware_version: string | null;
  is_template: boolean | null;
  host_name: string | null;
  vm_uuid: string | null;
  snapshot_count: number | null;
  oldest_snapshot_at: string | null;
  updated_at: string;
}

interface AssetVulnerability {
  id: number;
  type: 'eol_os' | 'vmware_advisory' | 'vmware_posture' | 'software_cve' | 'eol_software' | 'epp_missing_patch' | 'acronis_va';
  ref: string;
  title: string;
  severity: 'low' | 'medium' | 'high' | 'critical';
  detail: string | null;
  detected_at: string;
}

const VULN_TYPE_LABELS: Record<AssetVulnerability['type'], string> = {
  eol_os: 'End of life (OS)', vmware_advisory: 'VMSA advisory', vmware_posture: 'Configuration',
  software_cve: 'Software CVE', eol_software: 'End of life (software)',
  epp_missing_patch: 'Missing patch (Sangfor EPP)', acronis_va: 'Acronis assessment',
};

// Riassunto dell'inventario hardware riportato dall'EDR (oggi Acronis, extra.hardware: vedi
// backend services/edr/acronis.ts parseHardware).
interface HardwareSummary {
  scannedAt?: string;
  bootTime?: string;
  osInstalledAt?: string;
  motherboard?: string;
  disks?: Array<{ model?: string; media?: string; sizeBytes?: number }>;
  rams?: Array<{ slot?: string; sizeBytes?: number }>;
  gpus?: string[];
  lastHotfix?: { name: string; installedAt: string };
  hotfixCount?: number;
  joinType?: string;
  logonServer?: string;
}

function formatBytes(n?: number): string | null {
  if (!n || n <= 0) return null;
  const gb = n / 1024 ** 3;
  return gb >= 1000 ? `${(gb / 1024).toFixed(1)} TB` : `${gb >= 10 ? Math.round(gb) : gb.toFixed(1)} GB`;
}

function daysAgo(iso?: string): number | null {
  if (!iso) return null;
  const t = new Date(iso).getTime();
  return isNaN(t) ? null : Math.floor((Date.now() - t) / 86400000);
}

// Un inventario hardware vecchio (scansione ferma da mesi) o un ultimo aggiornamento di Windows
// lontano sono informazioni per l'analista: evidenziati in ambra oltre 30 e 60 giorni.
function HardwareSection({ hw }: { hw: HardwareSummary }) {
  const scanAge = daysAgo(hw.scannedAt);
  const hotfixAge = daysAgo(hw.lastHotfix?.installedAt);
  const rows: Array<[string, React.ReactNode]> = [];
  if (hw.motherboard) rows.push(['Motherboard', hw.motherboard]);
  if (hw.disks?.length) rows.push(['Disks', hw.disks.map(d => [d.model, d.media, formatBytes(d.sizeBytes)].filter(Boolean).join(' · ')).join('; ')]);
  if (hw.rams?.length) rows.push(['RAM modules', hw.rams.map(r => [r.slot, formatBytes(r.sizeBytes)].filter(Boolean).join(' ')).join('; ')]);
  if (hw.gpus?.length) rows.push(['GPU', hw.gpus.join('; ')]);
  if (hw.lastHotfix) {
    rows.push(['Last Windows update', (
      <span className={hotfixAge !== null && hotfixAge > 60 ? 'text-amber-700 font-medium' : ''}>
        {hw.lastHotfix.name} · {new Date(hw.lastHotfix.installedAt).toLocaleDateString('en-GB')}
        {hotfixAge !== null ? ` (${hotfixAge} days ago)` : ''}{hw.hotfixCount ? ` · ${hw.hotfixCount} installed` : ''}
      </span>
    )]);
  }
  if (hw.bootTime) rows.push(['Last boot', new Date(hw.bootTime).toLocaleString('en-GB')]);
  if (hw.osInstalledAt) rows.push(['OS installed', new Date(hw.osInstalledAt).toLocaleDateString('en-GB')]);
  if (hw.joinType || hw.logonServer) rows.push(['Domain join', [hw.joinType, hw.logonServer ? `logon server ${hw.logonServer}` : null].filter(Boolean).join(' · ')]);
  if (hw.scannedAt) {
    rows.push(['Hardware inventory', (
      <span className={scanAge !== null && scanAge > 30 ? 'text-amber-700 font-medium' : ''}>
        scanned {new Date(hw.scannedAt).toLocaleDateString('en-GB')}{scanAge !== null && scanAge > 30 ? ` (${scanAge} days ago, may be outdated)` : ''}
      </span>
    )]);
  }
  if (!rows.length) return null;
  return (
    <div className="mt-4 pt-3 border-t border-gray-100">
      <h3 className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">Hardware inventory</h3>
      <dl className="grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-3 text-sm">
        {rows.map(([label, value]) => (
          <div key={label}>
            <dt className="text-xs text-gray-400">{label}</dt>
            <dd className="text-gray-800 break-words">{value}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
}

interface EdrDetail {
  vendor: string;
  agent_id: string | null;
  agent_version: string | null;
  online: boolean | null;
  up_to_date: boolean | null;
  isolated: boolean | null;
  infected: boolean | null;
  active_threats: number | null;
  last_user: string | null;
  domain: string | null;
  machine_type: string | null;
  manufacturer: string | null;
  model: string | null;
  serial_number: string | null;
  os_name: string | null;
  os_version: string | null;
  os_arch: string | null;
  cpu: string | null;
  cpu_count: number | null;
  memory_mb: number | null;
  external_ip: string | null;
  site_group: string | null;
  edr_last_seen_at: string | null;
  last_scan_at: string | null;
  registered_at: string | null;
  extra: Record<string, unknown>;
  software_synced_at: string | null;
  updated_at: string;
}

interface SoftwareItem {
  name: string;
  version: string;
  publisher: string | null;
  installed_at: string | null;
  source: string;
}

const EDR_LABELS: Record<string, string> = {
  sentinelone: 'SentinelOne', crowdstrike: 'CrowdStrike Falcon', bitdefender: 'Bitdefender GravityZone',
  acronis: 'Acronis Cyber Protect', sangfor_xdr: 'Sangfor Athena XDR', sangfor_epp: 'Sangfor Athena EPP',
};

function formatMemory(mb: number | null): string | null {
  if (mb === null) return null;
  return mb >= 1024 ? `${(mb / 1024).toFixed(mb % 1024 === 0 ? 0 : 1)} GB` : `${mb} MB`;
}

function formatDate(iso: string | null): string {
  return iso ? new Date(iso).toLocaleString('en-GB') : '—';
}

// Scheda "Endpoint protection" per un vendor: stato dell'agent come badge, poi i dettagli in
// griglia. I campi vendor-specifici non mappati (extra) sono elencati in coda, così come
// arrivano, per non perdere informazione.
function EdrDetailCard({ d }: { d: EdrDetail }) {
  const danger = d.infected || d.isolated;
  const rows: Array<[string, React.ReactNode]> = [
    ['Agent version', d.agent_version || '—'],
    ['Last user', d.last_user || '—'],
    ['Domain', d.domain || '—'],
    ['Machine type', d.machine_type || '—'],
    ['Manufacturer / model', [d.manufacturer, d.model].filter(Boolean).join(' ') || '—'],
    ['Serial number', d.serial_number ? <span className="font-mono">{d.serial_number}</span> : '—'],
    ['Operating system', [d.os_name, d.os_version, d.os_arch ? `(${d.os_arch})` : null].filter(Boolean).join(' ') || '—'],
    ['CPU', [d.cpu, d.cpu_count ? `${d.cpu_count} cores` : null].filter(Boolean).join(', ') || '—'],
    ['Memory', formatMemory(d.memory_mb) || '—'],
    ['External IP', d.external_ip ? <span className="font-mono">{d.external_ip}</span> : '—'],
    ['Site / group', d.site_group || '—'],
    ['Last seen by EDR', formatDate(d.edr_last_seen_at)],
    ['Last scan', formatDate(d.last_scan_at)],
    ['Agent registered', formatDate(d.registered_at)],
  ];
  const hardware = d.extra?.hardware && typeof d.extra.hardware === 'object' ? d.extra.hardware as HardwareSummary : null;
  const extras = Object.entries(d.extra || {}).filter(([k, v]) => k !== 'hardware' && v !== null && v !== undefined && v !== '' && !(Array.isArray(v) && v.length === 0));
  return (
    <div className="card p-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h2 className="font-medium text-sm text-gray-900 inline-flex items-center gap-2">
          {danger ? <ShieldAlert size={15} className="text-red-600" /> : <ShieldCheck size={15} className="text-emerald-600" />}
          Endpoint protection — {EDR_LABELS[d.vendor] || d.vendor}
        </h2>
        <div className="flex items-center gap-1.5">
          {d.online !== null && (
            <span className={`badge ${d.online ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-500'}`}>{d.online ? 'Agent online' : 'Agent offline'}</span>
          )}
          {d.up_to_date === false && <span className="badge bg-amber-100 text-amber-700">Agent outdated</span>}
          {d.isolated && <span className="badge bg-red-100 text-red-700">Network isolated</span>}
          {d.infected && <span className="badge bg-red-100 text-red-700">Infected{d.active_threats ? ` · ${d.active_threats} active threat(s)` : ''}</span>}
        </div>
      </div>
      <dl className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-3 text-sm">
        {rows.map(([label, value]) => (
          <div key={label as string}>
            <dt className="text-xs text-gray-400">{label}</dt>
            <dd className="text-gray-800 break-words">{value}</dd>
          </div>
        ))}
      </dl>
      {hardware && <HardwareSection hw={hardware} />}
      {extras.length > 0 && (
        <details className="mt-3 text-xs">
          <summary className="cursor-pointer text-gray-500">More vendor fields ({extras.length})</summary>
          <dl className="mt-2 grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-2">
            {extras.map(([k, v]) => (
              <div key={k}>
                <dt className="text-gray-400">{k}</dt>
                <dd className="text-gray-700 font-mono break-all">{typeof v === 'object' ? JSON.stringify(v) : String(v)}</dd>
              </div>
            ))}
          </dl>
        </details>
      )}
      <p className="mt-3 text-xs text-gray-400">Updated {formatDate(d.updated_at)}</p>
    </div>
  );
}

// Scheda vSphere: per un host ESXi versione/build e la postura (lockdown, SSH, Shell, opzioni
// avanzate) con i valori deboli evidenziati — gli stessi che generano i rilievi in Vulnerabilities;
// per una VM guest OS, Tools, snapshot e host che la ospita; per un vCenter versione e build.
function VmwareDetailCard({ d }: { d: VmwareDetail }) {
  const yesNo = (v: boolean | null, badWhen: boolean, labels: [string, string] = ['Yes', 'No']) => {
    if (v === null) return <span className="text-gray-400">n/a</span>;
    const bad = v === badWhen;
    return <span className={bad ? 'text-red-600 font-medium' : 'text-emerald-700'}>{v ? labels[0] : labels[1]}</span>;
  };
  const kindLabel = d.kind === 'host' ? 'ESXi host' : d.kind === 'vm' ? (d.is_template ? 'VM template' : 'Virtual machine') : 'vCenter Server';
  let rows: Array<[string, React.ReactNode]>;
  if (d.kind === 'host') {
    rows = [
      ['Version / build', d.product_version ? <span>{d.product_version} <span className="font-mono text-xs text-gray-500">build {d.product_build}</span></span> : '—'],
      ['Connection', d.connection_state ? <span className={d.connection_state === 'connected' ? '' : 'text-red-600'}>{d.connection_state}{d.maintenance_mode ? ' · maintenance mode' : ''}</span> : '—'],
      ['Management IP', d.management_ip ? <span className="font-mono">{d.management_ip}</span> : '—'],
      ['Hardware', [d.hardware_vendor, d.hardware_model].filter(Boolean).join(' ') || '—'],
      ['CPU', [d.cpu_model, d.cpu_count ? `${d.cpu_count} cores` : null].filter(Boolean).join(', ') || '—'],
      ['Memory', formatMemory(d.memory_mb) || '—'],
      ['Last boot', formatDate(d.boot_time)],
      ['Lockdown mode', d.lockdown_mode ? <span className={d.lockdown_mode === 'lockdownDisabled' ? 'text-red-600 font-medium' : 'text-emerald-700'}>{d.lockdown_mode.replace(/^lockdown/, '')}</span> : <span className="text-gray-400">n/a</span>],
      ['SSH service', d.ssh_running === null ? <span className="text-gray-400">n/a</span> : <span className={d.ssh_running ? 'text-red-600 font-medium' : 'text-emerald-700'}>{d.ssh_running ? 'running' : 'stopped'}{d.ssh_policy ? ` · policy ${d.ssh_policy}` : ''}</span>],
      ['ESXi Shell', d.shell_running === null ? <span className="text-gray-400">n/a</span> : <span className={d.shell_running ? 'text-red-600 font-medium' : 'text-emerald-700'}>{d.shell_running ? 'running' : 'stopped'}{d.shell_policy ? ` · policy ${d.shell_policy}` : ''}</span>],
      ['execInstalledOnly', yesNo(d.exec_installed_only, false, ['enabled', 'disabled'])],
      ['Managed Object Browser', yesNo(d.mob_enabled, true, ['enabled', 'disabled'])],
      ['Account lockout', d.account_lock_failures === null ? <span className="text-gray-400">n/a</span> : d.account_lock_failures === 0 ? <span className="text-red-600 font-medium">disabled</span> : `after ${d.account_lock_failures} failures`],
      ['Remote syslog', d.syslog_host ? <span className="font-mono">{d.syslog_host}</span> : <span className="text-red-600 font-medium">not configured</span>],
      ['NTP', d.ntp_servers && d.ntp_servers.length ? d.ntp_servers.join(', ') : <span className="text-red-600 font-medium">not configured</span>],
    ];
  } else if (d.kind === 'vm') {
    rows = [
      ['VM name', d.name],
      ['Power state', d.power_state || '—'],
      ['Guest OS', d.guest_full_name || '—'],
      ['Guest hostname / IP', [d.guest_hostname, d.guest_ip].filter(Boolean).join(' · ') || '—'],
      ['VMware Tools', d.tools_status ? <span className={/old|notinstalled|blacklisted|needupgrade|tooold/i.test(`${d.tools_status} ${d.tools_version_status || ''}`) ? 'text-amber-700' : ''}>{d.tools_status}{d.tools_version ? ` · v${d.tools_version}` : ''}{d.tools_version_status ? ` · ${d.tools_version_status.replace(/^guestTools/, '')}` : ''}</span> : '—'],
      ['Hardware version', d.hardware_version || '—'],
      ['vCPU / memory', [d.cpu_count ? `${d.cpu_count} vCPU` : null, formatMemory(d.memory_mb)].filter(Boolean).join(' · ') || '—'],
      ['Snapshots', d.snapshot_count ? <span className={d.oldest_snapshot_at && Date.now() - new Date(d.oldest_snapshot_at).getTime() > 30 * 86400000 ? 'text-amber-700' : ''}>{d.snapshot_count} (oldest {formatDate(d.oldest_snapshot_at)})</span> : 'none'],
      ['Runs on host', d.host_name || '—'],
      ['VM UUID', d.vm_uuid ? <span className="font-mono text-xs">{d.vm_uuid}</span> : '—'],
    ];
  } else {
    rows = [
      ['Product', d.product_name || '—'],
      ['Version / build', d.product_version ? <span>{d.product_version} <span className="font-mono text-xs text-gray-500">build {d.product_build}</span></span> : '—'],
    ];
  }
  return (
    <div className="card p-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h2 className="font-medium text-sm text-gray-900 inline-flex items-center gap-2">
          <Layers size={15} className="text-sky-600" /> VMware vSphere — {kindLabel}
        </h2>
        <span className="text-xs text-gray-400">via {d.integration_label || d.integration_url}</span>
      </div>
      <dl className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-3 text-sm">
        {rows.map(([label, value]) => (
          <div key={label as string}>
            <dt className="text-xs text-gray-400">{label}</dt>
            <dd className="text-gray-800 break-words">{value}</dd>
          </div>
        ))}
      </dl>
      <p className="mt-3 text-xs text-gray-400">Updated {formatDate(d.updated_at)}</p>
    </div>
  );
}

function VulnerabilitiesCard({ items }: { items: AssetVulnerability[] }) {
  return (
    <div className="card p-4">
      <h2 className="font-medium text-sm text-gray-900 mb-2 inline-flex items-center gap-2">
        <ShieldOff size={15} className="text-red-600" /> Open vulnerabilities ({items.length})
      </h2>
      <ul className="divide-y divide-gray-100">
        {items.map(v => (
          <li key={v.id} className="py-2 text-sm">
            <div className="flex items-center gap-2 flex-wrap">
              <span className={`badge ${SEVERITY_BADGE[v.severity]}`}>{v.severity}</span>
              <span className="badge bg-gray-100 text-gray-500">{VULN_TYPE_LABELS[v.type] || v.type}</span>
              <span className="font-medium text-gray-800">{v.title}</span>
              <span className="text-xs text-gray-400 ml-auto">since {new Date(v.detected_at).toLocaleDateString('en-GB')}</span>
            </div>
            {v.detail && <p className="text-xs text-gray-500 mt-1">{v.detail}</p>}
          </li>
        ))}
      </ul>
    </div>
  );
}

function SoftwareCard({ items }: { items: SoftwareItem[] }) {
  const [query, setQuery] = useState('');
  const [showAll, setShowAll] = useState(false);
  const q = query.trim().toLowerCase();
  const filtered = q ? items.filter(s => `${s.name} ${s.publisher || ''}`.toLowerCase().includes(q)) : items;
  const shown = showAll ? filtered : filtered.slice(0, 50);
  return (
    <div className="card overflow-hidden">
      <div className="px-4 py-2.5 border-b border-gray-100 flex items-center justify-between gap-3 flex-wrap">
        <h2 className="font-medium text-sm text-gray-900 inline-flex items-center gap-2">
          <Package size={15} /> Installed software <span className="text-gray-400 font-normal">({items.length})</span>
        </h2>
        <input value={query} onChange={e => setQuery(e.target.value)} placeholder="Search name or publisher…"
          className="input-field py-1 text-xs w-56" />
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr>
            <th className="table-header">Name</th><th className="table-header">Version</th>
            <th className="table-header">Publisher</th><th className="table-header">Installed</th><th className="table-header">Source</th>
          </tr></thead>
          <tbody>
            {shown.map(s => (
              <tr key={`${s.name}|${s.version}`} className="border-t border-gray-100">
                <td className="table-cell">{s.name}</td>
                <td className="table-cell font-mono text-xs text-gray-600">{s.version || '—'}</td>
                <td className="table-cell text-gray-500">{s.publisher || '—'}</td>
                <td className="table-cell text-gray-500">{s.installed_at ? new Date(s.installed_at).toLocaleDateString('en-GB') : '—'}</td>
                <td className="table-cell text-gray-400">{EDR_LABELS[s.source] || s.source}</td>
              </tr>
            ))}
            {shown.length === 0 && <tr><td className="table-cell text-gray-400" colSpan={5}>No match</td></tr>}
          </tbody>
        </table>
      </div>
      {filtered.length > shown.length && (
        <button onClick={() => setShowAll(true)} className="w-full text-xs text-primary-600 py-2 border-t border-gray-100 hover:bg-gray-50">
          Show all {filtered.length}
        </button>
      )}
    </div>
  );
}

interface AssetCase {
  id: number;
  title: string;
  status: string;
  severity: string;
  mitre_tactic: string | null;
  created_at: string;
  alerts_count: number;
}

interface AssetLogSource {
  id: number;
  sender_ip: string;
  hostname: string;
  vendor: string | null;
  product: string | null;
  device_type: string;
  label: string | null;
  event_count: number;
  last_seen_at: string;
  broker_name: string;
}

interface AssetChange {
  field: 'hostname' | 'ip_address' | 'mac_address';
  old_value: string | null;
  new_value: string | null;
  changed_at: string;
}

interface RiskScoreContribution {
  case_id: number;
  severity: string;
  weight: number;
  age_days: number;
  via: 'device' | 'identity';
}

interface AssetDetail {
  id: number;
  tenant_id: number;
  tenant_name: string;
  hostname: string;
  ip_address: string | null;
  mac_address: string | null;
  // Altre schede di rete note della stessa macchina (Wi-Fi di un PC cablato, MAC precedente
  // dopo una fusione): le letture dell'NDR su queste schede finiscono su questo asset.
  secondary_macs: Array<{ mac: string; vendor: string | null; source: string; last_ip: string | null; last_seen_at: string }>;
  // Interruzioni rilevate dal monitor (asset business critical o in un gruppo monitorato) e
  // disponibilità a 30 giorni.
  outages: Array<{ id: number; case_id: number | null; cause: 'silence' | 'ping' | 'tcp'; reason: string | null;
    started_at: string; ended_at: string | null; duration_seconds: number }>;
  availability_30d: number;
  downtime_30d_seconds: number;
  group_monitor_reachability?: boolean | null;
  vendor: string | null;
  device_type: string | null;
  owner: string | null;
  notes: string | null;
  business_role: string | null;
  night_allowed: boolean;
  group_name: string | null;
  active_check_ok: boolean | null;
  active_check_at: string | null;
  active_check_rtt_ms: number | null;
  check_tcp_port: number | null;
  active_check_tcp_ok: boolean | null;
  tags: string[];
  business_critical: boolean;
  first_seen_at: string;
  last_seen_at: string;
  silent_minutes: number | null;
  reachable: boolean;
  frequency: 'habitual' | 'occasional';
  cases: AssetCase[];
  log_sources: AssetLogSource[];
  seen_days: string[];
  changes: AssetChange[];
  risk_score: number;
  risk_score_breakdown: RiskScoreContribution[];
  edr_details: EdrDetail[];
  software: SoftwareItem[];
  vmware_details: VmwareDetail[];
  vulnerabilities: AssetVulnerability[];
  remote_access_policy: 'inherit' | 'deny' | 'allow';
  remote_access_tools: string[];
  remote_access_last_seen_at: string | null;
  remote_access_last_tool: string | null;
  remote_access_last_signal: string | null;
}

const SEVERITY_BADGE: Record<string, string> = {
  low: 'bg-gray-100 text-gray-600', medium: 'bg-amber-100 text-amber-700',
  high: 'bg-orange-100 text-orange-700', critical: 'bg-red-100 text-red-700',
};
const STATUS_BADGE: Record<string, string> = {
  new: 'bg-blue-100 text-blue-700', investigating: 'bg-purple-100 text-purple-700',
  contained: 'bg-teal-100 text-teal-700', closed: 'bg-gray-100 text-gray-500',
};

function riskScoreStyle(score: number): string {
  if (score >= 70) return 'bg-red-100 text-red-700';
  if (score >= 40) return 'bg-amber-100 text-amber-700';
  if (score > 0) return 'bg-sky-50 text-sky-700';
  return 'bg-gray-100 text-gray-400';
}

// Griglia di presenza degli ultimi 30 giorni (stile "contribution graph"): un quadratino per
// giorno, pieno se l'asset è stato visto negli eventi almeno una volta quel giorno.
function PresenceCalendar({ seenDays }: { seenDays: string[] }) {
  const seen = new Set(seenDays.map(d => d.slice(0, 10)));
  const days: { date: Date; key: string }[] = [];
  for (let i = 29; i >= 0; i--) {
    const date = new Date();
    date.setDate(date.getDate() - i);
    days.push({ date, key: date.toISOString().slice(0, 10) });
  }
  return (
    <div className="flex flex-wrap gap-1">
      {days.map(({ date, key }) => (
        <div key={key}
          title={`${date.toLocaleDateString('en-GB')} — ${seen.has(key) ? 'seen' : 'not seen'}`}
          className={`w-4 h-4 rounded-sm ${seen.has(key) ? 'bg-emerald-500' : 'bg-gray-200'}`} />
      ))}
    </div>
  );
}

export default function AssetDetailPage() {
  const { assetId } = useParams();
  const [asset, setAsset] = useState<AssetDetail | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    axios.get(`/api/assets/${assetId}`)
      .then(res => setAsset(res.data.data))
      .catch(err => setError(err.response?.data?.error || 'Error while loading the asset'));
  }, [assetId]);

  if (error) return <div className="card p-4 text-sm text-red-600 bg-red-50">{error}</div>;
  if (!asset) return <div className="card p-4 text-gray-400 text-sm">Loading…</div>;

  const info: Array<[string, React.ReactNode]> = [
    ['MAC Address', asset.mac_address ? <span className="font-mono">{asset.mac_address}</span> : '—'],
    ...((asset.secondary_macs || []).length > 0 ? [[
      'Other network cards',
      <span className="font-mono text-xs" title="Other network cards of the same machine (reported by the EDR or kept after a merge): NDR readings on these cards are attributed to this asset.">
        {asset.secondary_macs.map(m => `${m.mac}${m.vendor ? ` (${m.vendor})` : ''}${m.last_ip ? ` last seen as ${m.last_ip}` : ''}`).join(', ')}
      </span>,
    ] as [string, React.ReactNode]] : []),
    ['IP Address', asset.ip_address ? <span className="font-mono">{asset.ip_address}</span> : '—'],
    ['Vendor', asset.vendor || '—'],
    ['Device Type', asset.device_type || '—'],
    ['Tenant', asset.tenant_name],
    ['Group', asset.group_name || '—'],
    ['Owner', asset.owner || '—'],
    ['Business Role', asset.business_role || '—'],
    ['Night-time', asset.night_allowed ? 'Authorised to stay on at night' : 'Expected off at night'],
    ['Active Check', asset.active_check_at
      ? `${asset.active_check_ok ? 'Ping OK' : 'Ping failed'}${asset.active_check_rtt_ms !== null ? ` (${asset.active_check_rtt_ms}ms)` : ''} · ${new Date(asset.active_check_at).toLocaleString('en-GB')}`
      : 'never'],
    ...(asset.check_tcp_port ? [['Service Port Check', `Port ${asset.check_tcp_port}: ${
      asset.active_check_tcp_ok === null ? 'not checked yet' : asset.active_check_tcp_ok ? 'OK' : 'failed'
    }`] as [string, React.ReactNode]] : []),
    ['First Seen', new Date(asset.first_seen_at).toLocaleString('en-GB')],
    ['Last Seen', new Date(asset.last_seen_at).toLocaleString('en-GB')],
  ];

  return (
    <div className="space-y-4">
      <Link to="/asset-center" className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-800">
        <ArrowLeft size={15} /> Asset Inventory
      </Link>

      <div className="card p-4">
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-xl font-semibold text-gray-900 inline-flex items-center gap-2">
              {asset.hostname}
              {asset.business_critical && (
                <span className="inline-flex items-center gap-1 badge bg-amber-50 text-amber-700">
                  <Star size={12} fill="currentColor" /> Business Critical
                </span>
              )}
              <span className={`inline-flex items-center gap-1 badge font-semibold ${riskScoreStyle(asset.risk_score)}`}
                title="Risk score: based on recent alerts, weighted by severity and recency">
                <Gauge size={12} /> Risk {asset.risk_score}
              </span>
            </h1>
            <div className="mt-1 flex items-center gap-2">
              {asset.reachable
                ? <span className="inline-flex items-center gap-1.5 badge bg-green-100 text-green-700"><Wifi size={11} /> Reachable</span>
                : <span className="inline-flex items-center gap-1.5 badge bg-red-100 text-red-700"><WifiOff size={11} /> Unreachable</span>}
              <span className={`badge ${asset.frequency === 'habitual' ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-600'}`}>
                {asset.frequency === 'habitual' ? 'Detected habitually' : 'Detected occasionally'}
              </span>
              {asset.tags.map(tag => (
                <span key={tag} className="badge bg-primary-50 text-primary-700 inline-flex items-center gap-1"><Tag size={10} /> {tag}</span>
              ))}
            </div>
          </div>
        </div>
        <dl className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-3 text-sm">
          {info.map(([label, value]) => (
            <div key={label as string}>
              <dt className="text-xs text-gray-400">{label}</dt>
              <dd className="text-gray-800">{value}</dd>
            </div>
          ))}
        </dl>
        {asset.notes && <p className="mt-3 text-sm text-gray-500 border-t border-gray-100 pt-3">{asset.notes}</p>}
      </div>

      {asset.vulnerabilities?.length > 0 && <VulnerabilitiesCard items={asset.vulnerabilities} />}

      <RemoteAccessCard asset={asset} onSaved={patch => setAsset(a => (a ? { ...a, ...patch } : a))} />

      {asset.edr_details.map(d => <EdrDetailCard key={d.vendor} d={d} />)}
      {asset.vmware_details?.map(d => <VmwareDetailCard key={`${d.integration_id}-${d.kind}`} d={d} />)}

      {(asset.business_critical || asset.group_monitor_reachability || (asset.outages || []).length > 0) && (
        <AvailabilityCard outages={asset.outages || []} availability={asset.availability_30d} downSeconds={asset.downtime_30d_seconds} />
      )}

      <div className="card p-4">
        <h2 className="font-medium text-sm text-gray-900 mb-2 inline-flex items-center gap-2"><History size={15} /> Presence — last 30 days</h2>
        <PresenceCalendar seenDays={asset.seen_days} />
      </div>

      {asset.software.length > 0 && <SoftwareCard items={asset.software} />}

      {asset.risk_score_breakdown.length > 0 && (
        <div className="card p-4">
          <h2 className="font-medium text-sm text-gray-900 mb-2 inline-flex items-center gap-2">
            <Gauge size={15} /> Risk score breakdown
          </h2>
          <p className="text-xs text-gray-400 mb-3">
            Top contributing incidents, weighted by severity and how recently they occurred (older alerts count less).
            <UserCircle2 size={11} className="inline mx-1 -mt-0.5" />marks a risk inherited from the asset owner's
            identity (e.g. a risky Microsoft 365 sign-in), not a direct alert on this device.
          </p>
          <div className="flex flex-wrap gap-2">
            {asset.risk_score_breakdown.map((c, i) => {
              const relatedCase = asset.cases.find(ac => ac.id === c.case_id);
              const content = (
                <>
                  {c.via === 'identity' && <UserCircle2 size={12} className="text-primary-500" />}
                  <span className={`badge ${SEVERITY_BADGE[c.severity]}`}>{c.severity}</span>
                  <span className="text-gray-600">{relatedCase?.title || `Case #${c.case_id}`}</span>
                  <span className="text-gray-400">· {c.age_days < 1 ? 'today' : `${Math.round(c.age_days)}d ago`}</span>
                </>
              );
              return relatedCase ? (
                <Link key={i} to={`/incidents/${c.case_id}`}
                  className="inline-flex items-center gap-1.5 text-xs border border-gray-200 rounded-md px-2 py-1 hover:bg-gray-50">
                  {content}
                </Link>
              ) : (
                <span key={i} className="inline-flex items-center gap-1.5 text-xs border border-gray-200 rounded-md px-2 py-1">
                  {content}
                </span>
              );
            })}
          </div>
        </div>
      )}

      <div className="card overflow-hidden">
        <div className="px-4 py-2.5 border-b border-gray-100">
          <h2 className="font-medium text-sm text-gray-900 inline-flex items-center gap-2"><FolderOpen size={15} /> Incidents involving this asset</h2>
        </div>
        <table className="w-full text-sm">
          <thead><tr>
            <th className="table-header">Title</th><th className="table-header">Severity</th>
            <th className="table-header">Status</th><th className="table-header">Alerts</th>
            <th className="table-header">Created</th>
          </tr></thead>
          <tbody>
            {asset.cases.map(c => (
              <tr key={c.id} className="border-t border-gray-100 hover:bg-gray-50">
                <td className="table-cell"><Link to={`/incidents/${c.id}`} className="text-primary-600 hover:underline font-medium">{c.title}</Link></td>
                <td className="table-cell"><span className={`badge ${SEVERITY_BADGE[c.severity]}`}>{c.severity}</span></td>
                <td className="table-cell"><span className={`badge ${STATUS_BADGE[c.status]}`}>{c.status}</span></td>
                <td className="table-cell text-gray-500">{c.alerts_count}</td>
                <td className="table-cell text-gray-500">{new Date(c.created_at).toLocaleString('en-GB')}</td>
              </tr>
            ))}
            {asset.cases.length === 0 && <tr><td className="table-cell text-gray-400" colSpan={5}>No incident involves this asset</td></tr>}
          </tbody>
        </table>
      </div>

      <div className="card overflow-hidden">
        <div className="px-4 py-2.5 border-b border-gray-100">
          <h2 className="font-medium text-sm text-gray-900 inline-flex items-center gap-2"><Radio size={15} /> Log sources with this hostname</h2>
        </div>
        <table className="w-full text-sm">
          <thead><tr>
            <th className="table-header">Sender IP</th><th className="table-header">Vendor / Product</th>
            <th className="table-header">Type</th><th className="table-header">Broker</th>
            <th className="table-header">Events</th><th className="table-header">Last Seen</th>
          </tr></thead>
          <tbody>
            {asset.log_sources.map(ls => (
              <tr key={ls.id} className="border-t border-gray-100">
                <td className="table-cell font-mono text-xs">{ls.sender_ip}</td>
                <td className="table-cell text-gray-700">{[ls.vendor, ls.product].filter(Boolean).join(' ') || '—'}</td>
                <td className="table-cell"><span className="badge bg-sky-50 text-sky-700">{ls.device_type}</span></td>
                <td className="table-cell text-gray-500">{ls.broker_name}</td>
                <td className="table-cell text-gray-500">{Number(ls.event_count).toLocaleString('en-GB')}</td>
                <td className="table-cell text-gray-500">{new Date(ls.last_seen_at).toLocaleString('en-GB')}</td>
              </tr>
            ))}
            {asset.log_sources.length === 0 && <tr><td className="table-cell text-gray-400" colSpan={6}>This asset is not a log source</td></tr>}
          </tbody>
        </table>
      </div>

      <div className="card overflow-hidden">
        <div className="px-4 py-2.5 border-b border-gray-100">
          <h2 className="font-medium text-sm text-gray-900 inline-flex items-center gap-2"><Server size={15} /> Hostname / IP change history</h2>
        </div>
        <table className="w-full text-sm">
          <thead><tr>
            <th className="table-header">Field</th><th className="table-header">From</th>
            <th className="table-header">To</th><th className="table-header">When</th>
          </tr></thead>
          <tbody>
            {asset.changes.map((ch, i) => (
              <tr key={i} className="border-t border-gray-100">
                <td className="table-cell text-gray-700">{ch.field === 'hostname' ? 'Hostname' : ch.field === 'mac_address' ? 'MAC Address' : 'IP Address'}</td>
                <td className="table-cell font-mono text-xs text-gray-500">{ch.old_value || '—'}</td>
                <td className="table-cell font-mono text-xs text-gray-800">{ch.new_value || '—'}</td>
                <td className="table-cell text-gray-500">{new Date(ch.changed_at).toLocaleString('en-GB')}</td>
              </tr>
            ))}
            {asset.changes.length === 0 && <tr><td className="table-cell text-gray-400" colSpan={4}>No change recorded: hostname and IP have been stable since first detection</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function formatDurationSeconds(sec: number): string {
  const minutes = Math.max(1, Math.round(sec / 60));
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  if (hours < 48) return `${hours}h ${minutes % 60}m`;
  return `${Math.floor(hours / 24)}d ${hours % 24}h`;
}

const OUTAGE_CAUSE: Record<string, string> = { silence: 'No events', ping: 'Ping failed', tcp: 'TCP port check failed' };

// Storico delle interruzioni (asset_outages) e disponibilità a 30 giorni, per gli asset monitorati.
function AvailabilityCard({ outages, availability, downSeconds }: {
  outages: AssetDetail['outages']; availability: number; downSeconds: number;
}) {
  const ongoing = outages.find(o => !o.ended_at);
  const tone = ongoing ? 'text-red-600' : availability >= 99.9 ? 'text-emerald-600' : availability >= 99 ? 'text-amber-600' : 'text-red-600';
  return (
    <div className="card p-4">
      <div className="flex flex-wrap items-baseline justify-between gap-2 mb-2">
        <h2 className="font-medium text-sm text-gray-900 inline-flex items-center gap-2"><History size={15} /> Availability — last 30 days</h2>
        <span className="text-sm">
          <span className={`font-semibold ${tone}`}>{availability.toFixed(2)}%</span>
          <span className="text-gray-500"> · downtime {downSeconds > 0 ? formatDurationSeconds(downSeconds) : 'none'}</span>
          {ongoing && <span className="badge ml-2 bg-red-100 text-red-700">unreachable now</span>}
        </span>
      </div>
      {outages.length === 0 ? (
        <p className="text-xs text-gray-400">No outage recorded in the last 90 days.</p>
      ) : (
        <table className="w-full text-sm">
          <thead><tr className="text-left text-xs text-gray-500">
            <th className="py-1">Unreachable since</th><th>Back online</th><th>Duration</th><th>Cause</th><th></th>
          </tr></thead>
          <tbody>
            {outages.map(o => (
              <tr key={o.id} className="border-t border-gray-100">
                <td className="py-1.5 text-gray-700">{new Date(o.started_at).toLocaleString('en-GB')}</td>
                <td className="text-gray-700">{o.ended_at ? new Date(o.ended_at).toLocaleString('en-GB') : <span className="text-red-600 font-medium">ongoing</span>}</td>
                <td className="text-gray-700">{formatDurationSeconds(o.duration_seconds)}</td>
                <td className="text-gray-500" title={o.reason || undefined}>{OUTAGE_CAUSE[o.cause] || o.cause}</td>
                <td className="text-right">{o.case_id && <Link to={`/incidents/${o.case_id}`} className="text-xs text-primary-600 hover:underline">Incident #{o.case_id}</Link>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <p className="text-xs text-gray-400 mt-2">Start = last time the asset was seen (or the failed check); end = the monitor cycle that found it reachable again (5-minute precision).</p>
    </div>
  );
}
